import { useLingui } from '@lingui/react';
import { EmployeeSelector } from '@/components/form/EmployeeSelector';

interface EmployeeSearchSelectProps {
  value?: string;
  onChange: (id: string | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
}

/**
 * Settling-in's employee picker.
 *
 * A thin wrapper over the shared `EmployeeSelector` (issue #1217) so there is
 * exactly ONE employee-picker implementation in the codebase. The props and the
 * rendered behaviour are unchanged; the `settlingIn.employee.*` message ids stay
 * here so the existing catalogue entries keep their place.
 */
export function EmployeeSearchSelect({
  value,
  onChange,
  disabled,
  placeholder,
  ariaLabel,
}: EmployeeSearchSelectProps) {
  const { i18n } = useLingui();

  return (
    <EmployeeSelector
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      ariaLabel={
        ariaLabel ?? i18n._({ id: 'settlingIn.employee.ariaLabel', message: 'Responsible employee' })
      }
      messages={{
        choose: i18n._({ id: 'settlingIn.employee.choose', message: 'Choose an employee...' }),
        search: i18n._({
          id: 'settlingIn.employee.searchPlaceholder',
          message: 'Search by name...',
        }),
        empty: i18n._({ id: 'settlingIn.employee.empty', message: 'No employees found' }),
        loading: i18n._({ id: 'settlingIn.employee.loading', message: 'Loading employees...' }),
        loadFailed: i18n._({
          id: 'settlingIn.employee.loadFailed',
          message: 'Employees could not be loaded. Close and reopen to retry.',
        }),
      }}
    />
  );
}
